import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  Image,
  File,
  Trash2,
  Download,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';
import { apiUpload, apiGet, apiDelete, apiDownloadUrl } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

// ── Types ───────────────────────────────────────────────────────────

interface Attachment {
  id: string;
  filename: string;
  filepath: string;
  mime_type: string;
  size_bytes: number;
  meeting_instance_id: string | null;
  task_id: string | null;
  reference_doc_id: string | null;
  created_at: string;
}

interface FileUploadProps {
  parentType: 'meeting' | 'task' | 'reference_doc';
  parentId: string;
  onCountChange?: (count: number) => void;
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType === 'application/pdf') return FileText;
  return File;
}

function parentIdKey(parentType: FileUploadProps['parentType']): string {
  switch (parentType) {
    case 'meeting':
      return 'meeting_instance_id';
    case 'task':
      return 'task_id';
    case 'reference_doc':
      return 'reference_doc_id';
  }
}

// ── Component ───────────────────────────────────────────────────────

export function FileUpload({ parentType, parentId, onCountChange }: FileUploadProps) {
  const toast = useToast();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load attachments when parentId changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const key = parentIdKey(parentType);
        const data = await apiGet<Attachment[]>(`/attachments?${key}=${parentId}`);
        if (!cancelled) setAttachments(data);
      } catch (err) {
        console.error('Failed to load attachments:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [parentType, parentId]);

  // Notify parent of attachment count changes
  useEffect(() => {
    onCountChange?.(attachments.length);
  }, [attachments.length, onCountChange]);

  const uploadFile = useCallback(
    async (file: globalThis.File) => {
      setError(null);
      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append(parentIdKey(parentType), parentId);

        const attachment = await apiUpload<Attachment>('/attachments', formData);
        setAttachments((prev) => [attachment, ...prev]);
        toast.success(`Uploaded ${file.name}`);
      } catch (err: any) {
        const msg = err.message || 'Upload failed';
        setError(msg);
        toast.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [parentType, parentId, toast],
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      // Upload files sequentially to avoid overwhelming the server
      const fileArray = Array.from(files);
      (async () => {
        for (const file of fileArray) {
          await uploadFile(file);
        }
      })();
    },
    [uploadFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDownload = useCallback(async (attachment: Attachment) => {
    try {
      const blobUrl = await apiDownloadUrl(`/attachments/${attachment.id}/download`);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay to ensure the download starts
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, []);

  const handleDelete = useCallback(async (id: string, filename: string) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await apiDelete(`/attachments/${id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      toast.success('Attachment deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete attachment');
    }
  }, [toast]);

  return (
    <div
      className={`space-y-3 rounded-lg transition-all duration-200 ${
        dragOver ? 'ring-2 ring-accent bg-accent/5' : ''
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative flex flex-col items-center justify-center gap-2
          px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer
          transition-all duration-200
          ${
            dragOver
              ? 'border-accent bg-accent/10 scale-[1.01]'
              : 'border-border hover:border-border-hover hover:bg-bg-hover/50'
          }
          ${uploading ? 'pointer-events-none opacity-60' : ''}
        `}
      >
        {uploading ? (
          <Loader2 size={24} className="text-accent animate-spin" />
        ) : (
          <Upload size={24} className="text-text-muted" />
        )}
        <div className="text-center">
          <p className="text-sm text-text-secondary">
            {uploading
              ? 'Uploading...'
              : dragOver
                ? 'Drop files here'
                : 'Drop files here or click to browse'}
          </p>
          <p className="text-xs text-text-muted mt-1">
            Max 50MB per file. Images, PDFs, documents, spreadsheets, archives.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            // Reset so the same file can be selected again
            e.target.value = '';
          }}
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-danger/10 border border-danger/30">
          <AlertCircle size={14} className="text-danger shrink-0" />
          <span className="text-sm text-danger flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-danger/60 hover:text-danger transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* File list */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 size={18} className="text-text-muted animate-spin" />
        </div>
      ) : attachments.length > 0 ? (
        <ul className="space-y-1">
          {attachments.map((attachment) => {
            const Icon = getFileIcon(attachment.mime_type);
            return (
              <li
                key={attachment.id}
                className="flex items-center gap-2.5 px-3 py-2 rounded-md bg-bg-surface hover:bg-bg-hover/50 transition-colors group"
              >
                <Icon size={16} className="text-text-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary truncate">
                    {attachment.filename}
                  </p>
                  <p className="text-xs text-text-muted">
                    {formatFileSize(attachment.size_bytes)}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleDownload(attachment)}
                    title="Download"
                    className="p-1 rounded text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
                  >
                    <Download size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(attachment.id, attachment.filename)}
                    title="Delete"
                    className="p-1 rounded text-text-muted hover:text-danger hover:bg-bg-hover transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
